import { useEffect, useRef, useState } from "react";
import type { SlideProps } from "@/engine/app/offsite-config";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { READ_ONLY_TITLE, useReadOnly, useStore } from "@/state/store";
import { api } from "@/api/client";
import { QrCode, zugangsAdresse, zugangsPin } from "./_zugang";

/**
 * Themenwahl — auf der Leinwand, vor dem Gremium.
 *
 * Der erste Entwurf hatte die Wahl in die Seitenleiste des Moderators gelegt.
 * Falsch: Worüber gearbeitet wird, entscheidet die Runde gemeinsam, und dafür
 * muss es alle sehen. Zwei Vorschläge sind vorbereitet, ein eigenes Thema lässt
 * sich hier eintragen.
 *
 * Gewählt ist, was als Rahmenthema gespeichert ist — dieselbe Quelle, die die
 * Gäste im ersten Formularfeld vorausgefüllt bekommen.
 */
export function SlideThema({ onJump }: SlideProps) {
  const { preparedTopics, waitingSlide } = useOffsiteConfig();
  const { state, dispatch } = useStore();
  const token = state.auth.token;
  const readOnly = useReadOnly();

  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  // Der Poll braucht den letzten bekannten Wert ohne in seinen Abhängigkeiten
  // zu stehen — sonst würde das Intervall bei jeder Wahl neu aufgesetzt.
  const gewaehltRef = useRef<string | null>(null);
  const [eigenes, setEigenes] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [uebernahmeBusy, setUebernahmeBusy] = useState(false);

  const vorbereitet = preparedTopics ?? [];
  /** Steht die Wahl auf dem selbst eingetragenen Thema? */
  const eigenesGewaehlt = !!eigenes.trim() && gewaehlt === eigenes.trim();

  useEffect(() => {
    gewaehltRef.current = gewaehlt;
  }, [gewaehlt]);

  /**
   * Gespeichertes Thema laden — und dranbleiben.
   *
   * Nicht nur beim Öffnen: Der Gruppenwechsel archiviert das Rahmenthema, und
   * ohne Poll stand danach das Thema der vorigen Gruppe weiter im Feld „Eigenes
   * Thema" und unter „Wir arbeiten an …". Ein leer gewordenes Thema muss die
   * Folie auch leeren.
   *
   * Die eigene Eingabe wird dabei nicht überschrieben, solange sie noch nicht
   * übernommen ist — sonst löschte der Poll dem Moderator das Getippte weg.
   */
  useEffect(() => {
    let cancelled = false;
    const laden = async () => {
      try {
        const r = await api.framing.get(token);
        if (cancelled) return;
        const topic = r.topic ?? "";
        setGewaehlt(topic || null);
        if (!topic) {
          // Zurückgesetzt: nur räumen, wenn im Feld das alte, übernommene Thema
          // steht — nicht, wenn gerade etwas Neues getippt wird.
          setEigenes((vorher) => (vorher && vorher === gewaehltRef.current ? "" : vorher));
          return;
        }
        // Ein Thema, das nicht aus der Vorauswahl kommt, war ein eigenes — dann
        // gehört es ins Feld, nicht bloß in die Markierung.
        if (!vorbereitet.some((t) => t.full === topic)) setEigenes(topic);
      } catch (e) {
        if (cancelled) return;
        console.error(e);
      }
    };
    laden();
    const id = window.setInterval(laden, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // vorbereitet stammt aus der Config und ist über die Sitzung stabil.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const waehlen = async (thema: string) => {
    const t = thema.trim();
    if (!t) return;
    setBusy(t);
    setFehler(null);
    try {
      const r = await api.framing.set(token, t);
      setGewaehlt(r.topic ?? t);
    } catch (e) {
      console.error(e);
      // Vor Publikum darf kein Thema als gewählt dastehen, das nirgends
      // gespeichert ist — die Gäste bekämen sonst ein anderes vorausgefüllt.
      setFehler("Nicht übernommen. Bitte noch einmal antippen.");
    } finally {
      setBusy(null);
    }
  };

  /**
   * Vom gewählten Thema in die Arbeitsphase.
   *
   * Öffnet das Prompten auf den Handys und blättert die Leinwand zurück auf
   * Adresse, PIN und QR-Code. Der Timer läuft dabei ausdrücklich noch nicht:
   * beim Übergang sind noch nicht alle im Formular, und acht Minuten, die
   * währenddessen ablaufen, sind verschenkt. Er startet auf eigenen Klick im
   * Moderatorpult.
   */
  const themaNutzen = async () => {
    if (!gewaehlt) return;
    setUebernahmeBusy(true);
    setFehler(null);
    try {
      const p = await api.phase.set(token, "working");
      dispatch({ type: "phase/set", phase: p });
      // SlideProps.onJump ist 1-basiert, die Config-Indizes sind 0-basiert.
      if (waitingSlide !== undefined) onJump?.(waitingSlide + 1);
    } catch (e) {
      console.error(e);
      setFehler("Nicht übernommen — das Prompten ist noch zu.");
    } finally {
      setUebernahmeBusy(false);
    }
  };

  return (
    <div className="deck-root ar-slide" style={{ background: "var(--ink-900)" }}>
      <div className="deck-bg" aria-hidden="true" />

      <header className="deck-head">
        <div className="deck-eyebrow rise rise-1" style={{ color: "var(--ink-400)" }}>
          Ihre Wahl
        </div>
        <h1 className="deck-title rise rise-2">Woran arbeiten wir?</h1>
      </header>

      <div className="ar-thema-spalten">
      <div className="ar-thema-liste rise rise-3">
        {vorbereitet.map((t) => {
          const aktiv = gewaehlt === t.full;
          return (
            <button
              key={t.short}
              type="button"
              className={`ar-thema-karte${aktiv ? " is-gewaehlt" : ""}`}
              onClick={() => waehlen(t.full)}
              disabled={busy !== null || readOnly}
              title={readOnly ? READ_ONLY_TITLE : undefined}
              aria-pressed={aktiv}
            >
              <span className="ar-thema-marke" aria-hidden="true">
                {aktiv ? "●" : "○"}
              </span>
              <span className="ar-thema-text">
                <span className="ar-thema-kurz">{t.short}</span>
                <span className="ar-thema-lang">{t.full}</span>
              </span>
            </button>
          );
        })}

        {/* Das dritte Thema. Kein Vorschlag, sondern das Feld für den Einwand
         *  „wir hätten da etwas anderes" — und genau deshalb steht es hier und
         *  nicht in einem Menü, das nur der Moderator sieht. */}
        <div
          className={`ar-thema-karte is-eigenes${
            eigenesGewaehlt ? " is-gewaehlt" : ""
          }`}
        >
          {/* Der Kreis ist hier ein echter Schalter, keine Verzierung.
           *  Vorher war er ein `<span aria-hidden>`: Wer ihn antippte — und vor
           *  der Runde tippt man auf den Kreis, nicht auf einen Knopf am
           *  Zeilenende — bekam keine Reaktion, und ein Zurückwechseln auf das
           *  eigene Thema war nur über „Nehmen" möglich. */}
          <button
            type="button"
            className="ar-thema-marke ar-thema-marke-knopf"
            onClick={() => waehlen(eigenes)}
            disabled={busy !== null || readOnly || !eigenes.trim()}
            aria-label={`Eigenes Thema wählen${eigenes.trim() ? `: ${eigenes.trim()}` : ""}`}
            aria-pressed={eigenesGewaehlt}
            title={
              readOnly
                ? READ_ONLY_TITLE
                : eigenes.trim()
                  ? "Dieses Thema nehmen"
                  : "Erst ein Thema eintragen"
            }
          >
            {eigenesGewaehlt ? "●" : "○"}
          </button>
          <span className="ar-thema-text">
            <span className="ar-thema-kurz">Eigenes Thema</span>
            <input
              type="text"
              className="ar-thema-feld"
              aria-label="Eigenes Thema"
              placeholder="Woran möchten Sie stattdessen arbeiten?"
              value={eigenes}
              disabled={busy !== null || readOnly}
              onChange={(e) => {
                setEigenes(e.target.value);
                setFehler(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") waehlen(eigenes);
              }}
            />
          </span>
          <button
            type="button"
            className="ar-thema-nehmen"
            onClick={() => waehlen(eigenes)}
            disabled={busy !== null || readOnly || !eigenes.trim() || eigenesGewaehlt}
          >
            {eigenesGewaehlt ? "Gewählt" : "Nehmen"}
          </button>
        </div>
      </div>

      {/* Zugang läuft nebenher weiter: Wer während der Themen-Diskussion
       *  dazukommt, soll sich noch anmelden können, ohne dass zurückgeblättert
       *  werden muss. Kleiner als auf der Warte-Folie — dort ist er die
       *  Hauptsache, hier Beiwerk. */}
      <aside className="ar-thema-zugang rise rise-4">
        <div className="ar-thema-zugang-titel">Noch nicht dabei?</div>
        <QrCode klein />
        <div className="ar-thema-zugang-text">
          <div className="ar-thema-zugang-url">{zugangsAdresse}</div>
          <div className="ar-thema-zugang-pin">
            <span>PIN</span>
            <strong>{zugangsPin || "— — — —"}</strong>
          </div>
        </div>
      </aside>
      </div>

      <div className="ar-thema-fuss-zeile rise rise-4">
        <p className="ar-thema-fuss" role="status">
          {fehler ? (
            <span className="ar-thema-fehler">{fehler}</span>
          ) : gewaehlt ? (
            <>
              Wir arbeiten an: <strong>{gewaehlt}</strong>
            </>
          ) : (
            "Bitte wählen Sie ein Thema."
          )}
        </p>

        <button
          type="button"
          className="ar-thema-nutzen"
          data-testid="thema-nutzen"
          onClick={themaNutzen}
          disabled={!gewaehlt || uebernahmeBusy || busy !== null || readOnly}
          title={
            readOnly
              ? READ_ONLY_TITLE
              : "Öffnet das Prompten auf den Handys — der Timer startet danach im Pult"
          }
        >
          {uebernahmeBusy ? "…" : "Thema nutzen"}
        </button>
      </div>
    </div>
  );
}
